import React, { useCallback, useEffect, useMemo } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { ArrowLeft, ChevronRight, FileText, Info } from "lucide-react";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { Link, useLocation, useParams } from "react-router-dom";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import KnowledgeHubLayout from "../../layouts/KnowledgeHubLayout";
import selectedKnowledgeHubIds from "./ids.json";
import knowledgeHubRecords from "./knowledge-hub-records.json";

const PRESIGNED_BASE_URL = import.meta.env.VITE_PRESIGNED_BASE_URL;
const BASE_URL = "https://paralegal.lk";

const slugify = (value) =>
	(value || "")
		.toLowerCase()
		.normalize("NFKD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/-{2,}/g, "-")
		.replace(/^-+|-+$/g, "");

const cleanMarkdownText = (value) =>
	(value || "")
		.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
		.replace(/[*_`~]/g, "")
		.replace(/\\([.#-])/g, "$1")
		.replace(/\s+/g, " ")
		.trim();

const capitalizeFirstLetter = (value) => {
	const text = cleanMarkdownText(value);
	const firstLetterIndex = text.search(/[A-Za-z]/);

	if (firstLetterIndex < 0) return text;

	return `${text.slice(0, firstLetterIndex)}${text[firstLetterIndex].toUpperCase()}${text.slice(firstLetterIndex + 1)}`;
};

const isMarkdownNoiseLine = (line) => {
	const value = line.trim();

	return !value
		|| /^-{3,}$/.test(value)
		|| /^#{1,6}\s+/.test(value)
		|| /^>/.test(value)
		|| /^\|/.test(value)
		|| /^\s*\|?[-:| ]{3,}\|?\s*$/.test(value);
};

const isBoilerplatePreview = (value) =>
	/^based on my research(?: of relevant case law and legislations)?,?/i.test(cleanMarkdownText(value));

const isPreviewLeadIn = (value) =>
	/^(in\s+summary|summary|relevant\s+provisions|reference(?:s)?(?:\s+for)?|key\s+points?)\s*:?\s*$/i.test(cleanMarkdownText(value))
	|| /^(reference(?:s)?\s+for|when\b.+:|in\s+principle,?\s+.+provided\s+that:)/i.test(cleanMarkdownText(value));

const truncatePreview = (value, maxLength = 240) => {
	const text = cleanMarkdownText(value);

	if (text.length <= maxLength) return text;

	const truncated = text.slice(0, maxLength).trim();
	const sentenceEnd = Math.max(
		truncated.lastIndexOf("."),
		truncated.lastIndexOf("?"),
		truncated.lastIndexOf("!")
	);

	if (sentenceEnd >= 90) return truncated.slice(0, sentenceEnd + 1);

	return `${truncated.replace(/[,;:\s]+$/, "")}...`;
};

const extractPreviewFromLines = (lines) => {
	for (let index = 0; index < lines.length; index += 1) {
		const line = lines[index];
		const value = line.trim();

		if (isMarkdownNoiseLine(value)) continue;

		if (/^[-*+]\s+/.test(value) || /^\d+\.\s+/.test(value)) {
			const itemText = cleanMarkdownText(value.replace(/^[-*+]\s+/, "").replace(/^\d+\.\s+/, ""));
			if (itemText && !isBoilerplatePreview(itemText) && !isPreviewLeadIn(itemText)) return truncatePreview(itemText);
			continue;
		}

		const paragraph = [value];
		let nextIndex = index + 1;

		while (nextIndex < lines.length && !isMarkdownNoiseLine(lines[nextIndex])) {
			const nextLine = lines[nextIndex].trim();
			if (/^[-*+]\s+/.test(nextLine) || /^\d+\.\s+/.test(nextLine)) break;
			paragraph.push(nextLine);
			nextIndex += 1;
		}

		const text = cleanMarkdownText(paragraph.join(" "));
		if (text && !isBoilerplatePreview(text) && !isPreviewLeadIn(text)) return truncatePreview(text);
	}

	return "";
};

const extractSectionPreview = (markdown, headingPattern) => {
	const lines = (markdown || "").split(/\r?\n/);

	for (let index = 0; index < lines.length; index += 1) {
		const match = lines[index].match(/^#{1,6}\s+(.+)$/);
		if (!match || !headingPattern.test(cleanMarkdownText(match[1]))) continue;

		const sectionLines = [];
		for (let nextIndex = index + 1; nextIndex < lines.length; nextIndex += 1) {
			if (/^#{1,6}\s+/.test(lines[nextIndex].trim())) break;
			sectionLines.push(lines[nextIndex]);
		}

		const preview = extractPreviewFromLines(sectionLines);
		if (preview) return preview;
	}

	return "";
};

const extractKnowledgeHubPreview = (markdown) =>
	extractSectionPreview(markdown, /\bsummary\b/i)
	|| extractSectionPreview(markdown, /\bconclusion\b/i)
	|| extractPreviewFromLines((markdown || "").split(/\r?\n/));

const extractHeadings = (markdown) =>
	(markdown || "")
		.split(/\r?\n/)
		.map((line) => line.match(/^(#{2,3})\s+(.+)$/))
		.filter(Boolean)
		.map((match) => {
			const label = cleanMarkdownText(match[2]);
			return {
				id: slugify(label),
				label,
				level: match[1].length,
			};
		})
		.filter((heading) => heading.id && heading.label);

const getChatRecordTitle = (record) =>
	capitalizeFirstLetter(record?.query || record?.query_fields?.original_query || record?.original_query || "Untitled research question");

const getChatRecordContent = (record) =>
	record?.final_answer || record?.finalAnswer || "";

const getChatRecordDescription = (record) => {
	const content = getChatRecordContent(record);
	return extractKnowledgeHubPreview(content)
		|| truncatePreview(getChatRecordTitle(record))
		|| "Read the full legal research note and cited authorities.";
};

const getChatRecordLookupKeys = (record) =>
	[record?.trace_id, record?.id].filter(Boolean);

const getTextContent = (children) =>
	React.Children.toArray(children)
		.map((child) => {
			if (typeof child === "string" || typeof child === "number") return child;
			if (React.isValidElement(child)) return getTextContent(child.props.children);
			return "";
		})
		.join("");

const isPdfHref = (href) => Boolean(href && /\.pdf(?:[?#].*)?$/i.test(href));

const createMarkdownComponents = (openPdfLink) => ({
	h1: ({ node, ...props }) => (
		<h2 className="mb-3 mt-8 font-display text-20 font-semibold text-pl-text-primary md:text-22" {...props} />
	),
	h2: ({ node, children, ...props }) => (
		<h2
			id={slugify(getTextContent(children))}
			className="mb-0 mt-8 scroll-mt-28 border-b border-pl-border pb-1 font-display text-20 font-semibold text-pl-text-primary first:mt-0 md:text-22"
			{...props}
		>
			{children}
		</h2>
	),
	h3: ({ node, children, ...props }) => (
		<h3
			id={slugify(getTextContent(children))}
			className="mb-2 mt-7 scroll-mt-28 text-16 font-semibold text-pl-text-primary"
			{...props}
		>
			{children}
		</h3>
	),
	h4: ({ node, ...props }) => (
		<h4 className="mb-2 mt-6 text-16 font-semibold text-pl-text-primary" {...props} />
	),
	p: ({ node, ...props }) => (
		<p className="mt-4 text-15 leading-8 text-pl-text-secondary" {...props} />
	),
	strong: ({ node, ...props }) => (
		<strong className="font-semibold text-pl-text-primary" {...props} />
	),
	ul: ({ node, ...props }) => (
		<ul className="mt-4 list-disc space-y-2 pl-6 text-15 leading-7 text-pl-text-secondary" {...props} />
	),
	ol: ({ node, ...props }) => (
		<ol className="mt-4 list-decimal space-y-2 pl-6 text-15 leading-7 text-pl-text-secondary" {...props} />
	),
	li: ({ node, ...props }) => <li {...props} />,
	blockquote: ({ node, ...props }) => (
		<blockquote className="mt-5 border-l-4 border-pl-accent/50 pl-4 italic text-pl-text-secondary" {...props} />
	),
	a: ({ node, ...props }) => (
		<a
			className="font-medium text-pl-accent underline underline-offset-2 hover:text-pl-accent-hover"
			onClick={(event) => {
				if (!isPdfHref(props.href)) return;
				event.preventDefault();
				openPdfLink(props.href);
			}}
			target="_blank"
			rel="noopener noreferrer"
			{...props}
		/>
	),
	code: ({ node, inline, className, children, ...props }) =>
		inline ? (
			<code className="rounded bg-pl-bg-subtle px-1 py-0.5 text-13" {...props}>
				{children}
			</code>
		) : (
			<code className={`block overflow-x-auto rounded-pl bg-pl-bg-subtle p-4 text-13${className ? ` ${className}` : ""}`} {...props}>
				{children}
			</code>
		),
	pre: ({ node, ...props }) => <pre className="mt-5 overflow-x-auto" {...props} />,
	table: ({ node, ...props }) => (
		<div className="mt-5 w-full overflow-x-auto rounded-pl border border-pl-border">
			<table className="w-full border-collapse text-left text-13" {...props} />
		</div>
	),
	thead: ({ node, ...props }) => <thead className="bg-pl-bg-subtle" {...props} />,
	tr: ({ node, ...props }) => <tr className="border-b border-pl-border last:border-b-0" {...props} />,
	th: ({ node, ...props }) => <th className="border-r border-pl-border px-3 py-2 font-semibold text-pl-text-primary last:border-r-0" {...props} />,
	td: ({ node, ...props }) => <td className="border-r border-pl-border px-3 py-2 align-top text-pl-text-secondary last:border-r-0" {...props} />,
	hr: ({ node, ...props }) => <hr className="my-7 border-pl-border" {...props} />,
});

const TableOfContents = ({ headings }) => (
	<nav aria-label="On this page" className="sticky top-[112px] border-l border-pl-border pl-5">
		<h2 className="mb-3 text-14 font-semibold text-pl-text-primary">On this page</h2>
		<ul className="space-y-2">
			{headings.map((heading) => (
				<li key={`${heading.level}-${heading.id}`}>
					<a
						href={`#${heading.id}`}
						className={`block text-13 leading-5 text-pl-text-tertiary hover:text-pl-accent ${heading.level === 3 ? "pl-3" : ""}`}
					>
						{heading.label}
					</a>
				</li>
			))}
		</ul>
	</nav>
);

const KnowledgeHub = () => {
	const { slug } = useParams();
	const location = useLocation();
	const { state, signIn, getAccessToken } = useAuthContext();

	const list = useMemo(() => {
		const recordsById = new Map();

		for (const record of knowledgeHubRecords.records || []) {
			for (const key of getChatRecordLookupKeys(record)) {
				recordsById.set(key, record);
			}
		}

		return selectedKnowledgeHubIds
			.map((selectedId, index) => {
				const record = recordsById.get(selectedId);
				if (!record) return null;

				const title = getChatRecordTitle(record);
				const content = getChatRecordContent(record);
				const titleSlug = slugify(title);

				if (!titleSlug || !content) return null;

				return {
					id: record.id,
					selectedId,
					mongoId: record.id,
					traceId: record.trace_id || "",
					taskId: record.task_id || "",
					createdAt: record.timestamp || "",
					title,
					description: getChatRecordDescription(record),
					content,
					slug: `${index + 1}-${titleSlug}`,
				};
			})
			.filter(Boolean);
	}, []);

	const active = useMemo(
		() => (slug ? list.find((item) => item.slug === slug || item.traceId === slug || item.mongoId === slug) || null : null),
		[list, slug]
	);
	const headings = useMemo(() => extractHeadings(active?.content), [active?.content]);
	const currentPath = `${location.pathname}${location.search}`;

	const openPDFWithPreSignedUrl = useCallback(async (originalLink) => {
		const newWindow = window.open("about:blank", "_blank");

		if (!PRESIGNED_BASE_URL) {
			if (newWindow) newWindow.location.href = originalLink;
			return;
		}

		try {
			let accessToken = "";
			try {
				accessToken = (await getAccessToken()) || "";
			} catch (tokenError) {
				console.error("Failed to get access token for KnowledgeHub PDF:", tokenError);
			}

			const response = await fetch(`${PRESIGNED_BASE_URL}/api/pdf/get-pdf-url`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
				},
				body: JSON.stringify({ link: originalLink }),
			});
			const data = await response.json();

			if (data.success && newWindow) {
				newWindow.location.href = data.presigned_url;
				return;
			}

			if (newWindow) newWindow.location.href = originalLink;
		} catch (error) {
			console.error("Error fetching KnowledgeHub pre-signed PDF URL:", error);
			if (newWindow) newWindow.location.href = originalLink;
		}
	}, [getAccessToken]);

	const openPdfLink = useCallback(async (href) => {
		if (!href) return;

		if (!state.isAuthenticated) {
			sessionStorage.setItem("redirectToDocument", href);
			sessionStorage.setItem("redirectAfterLogin", currentPath);
			signIn();
			return;
		}

		await openPDFWithPreSignedUrl(href);
	}, [currentPath, openPDFWithPreSignedUrl, signIn, state.isAuthenticated]);

	const markdownComponents = useMemo(
		() => createMarkdownComponents(openPdfLink),
		[openPdfLink]
	);

	useEffect(() => {
		if (!state.isAuthenticated) return;

		const redirectToDocument = sessionStorage.getItem("redirectToDocument");
		const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");

		if (!redirectToDocument || redirectAfterLogin !== currentPath) return;

		openPDFWithPreSignedUrl(redirectToDocument);
		sessionStorage.removeItem("redirectToDocument");
		sessionStorage.removeItem("redirectAfterLogin");
	}, [currentPath, openPDFWithPreSignedUrl, state.isAuthenticated]);

	const pageTitle = active
		? `${active.title} | Knowledge Hub | paralegal.lk`
		: "Knowledge Hub | paralegal.lk";
	const pageDescription = active?.description
		|| "Explore detailed legal research, case law, and cited answers on Sri Lankan law.";

	if (slug && !active) {
		return (
			<KnowledgeHubLayout>
				<Helmet>
					<title>Research question not found | Knowledge Hub | paralegal.lk</title>
					<meta name="robots" content="noindex" />
				</Helmet>
				<section className="pb-20">
					<Link to="/knowledge-hub" className="mb-5 inline-flex items-center gap-2 text-14 text-pl-text-tertiary hover:text-pl-accent">
						<ArrowLeft size={16} aria-hidden="true" />
						All research questions
					</Link>
					<div className="rounded-pl-lg border border-pl-border bg-pl-bg-elevated p-6">
						<h1 className="font-display text-24 font-semibold text-pl-text-primary">Research question not found</h1>
						<p className="mt-3 text-15 text-pl-text-secondary">This research note may have moved or is no longer available.</p>
					</div>
				</section>
			</KnowledgeHubLayout>
		);
	}

	return (
		<div className="relative">
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
				<link rel="canonical" href={`${BASE_URL}${location.pathname}`} />
				{active && (
					<script type="application/ld+json">{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "QAPage",
						mainEntity: {
							"@type": "Question",
							name: active.title,
							answerCount: 1,
							acceptedAnswer: {
								"@type": "Answer",
								text: active.content,
							},
						},
					})}</script>
				)}
			</Helmet>

			<KnowledgeHubLayout
				topContent={active ? (
					<nav aria-label="Breadcrumb" className="mb-5 flex items-center gap-2 text-14 text-pl-text-tertiary">
						<Link to="/knowledge-hub" className="hover:text-pl-accent">Knowledge Hub</Link>
						<ChevronRight size={14} aria-hidden="true" />
						<span aria-current="page">Research question</span>
					</nav>
				) : null}
				sidebar={active && headings.length > 0 ? <TableOfContents headings={headings} /> : null}
			>
				{!active ? (
					<section className="pb-20">
						<header className="border-b border-pl-border pb-8">
							<h1 className="font-display text-28 font-semibold text-pl-text-primary md:text-36">Knowledge Hub</h1>
							<p className="mt-3 max-w-2xl text-16 leading-7 text-pl-text-secondary">
								Explore detailed legal research, case law, and cited answers on Sri Lankan law.
							</p>
						</header>

						<div className="mt-8">
							<div className="mb-3 flex items-baseline justify-between gap-4">
								<h2 className="font-display text-22 font-semibold text-pl-text-primary">Research questions</h2>
								<span className="text-13 text-pl-text-tertiary">{list.length} articles</span>
							</div>
							<ul className="border-t border-pl-border">
								{list.map((item) => (
									<li key={item.id} className="border-b border-pl-border">
										<Link
											to={`/knowledge-hub/${item.slug}`}
											className="group grid min-h-[76px] grid-cols-[24px_minmax(0,1fr)_20px] gap-3 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-pl-accent"
										>
											<FileText className="mt-1 text-pl-text-tertiary group-hover:text-pl-accent" size={18} aria-hidden="true" />
											<div className="min-w-0">
												<h3 className="text-16 font-normal leading-6 text-pl-text-primary group-hover:text-pl-accent md:text-18">
													{item.title}
												</h3>
											</div>
											<ChevronRight className="mt-1 text-pl-text-tertiary group-hover:text-pl-accent" size={18} aria-hidden="true" />
										</Link>
									</li>
								))}
							</ul>
						</div>
					</section>
				) : (
					<section className="pb-20">
						{headings.length > 0 && (
							<details className="mb-4 rounded-pl border border-pl-border bg-pl-bg-elevated px-4 py-3 lg:hidden">
								<summary className="cursor-pointer text-14 font-semibold text-pl-text-primary">On this page</summary>
								<ul className="mt-3 space-y-2 border-t border-pl-border pt-3">
									{headings.map((heading) => (
										<li key={`${heading.level}-${heading.id}`}>
											<a href={`#${heading.id}`} className={`block text-13 leading-5 text-pl-text-tertiary hover:text-pl-accent ${heading.level === 3 ? "pl-3" : ""}`}>
												{heading.label}
											</a>
										</li>
									))}
								</ul>
							</details>
						)}

						<article className="overflow-hidden rounded-pl-lg border border-pl-border bg-pl-bg-elevated shadow-pl-sm">
							<header className="border-b border-pl-border px-5 py-6 md:px-7 md:py-7">
								<p className="text-13 font-medium text-pl-accent">Research question</p>
								<h1 className="mt-2 font-display text-24 font-semibold leading-8 text-pl-text-primary md:text-28 md:leading-9">
									{active.title}
								</h1>
								<div role="note" className="mt-5 flex items-start gap-2 border-t border-pl-border pt-4 text-13 leading-5 text-pl-text-tertiary">
									<Info className="mt-0.5 flex-none" size={15} aria-hidden="true" />
									<div>
										<p>This answer was produced by our advanced AI legal research agent Researcher Silva 2.</p>
										<p className="mt-2">
											To gain access to this agent and our legal AI workspace, become a professional member at: {"  "}
											<Link to="/apply-for-membership" className="font-medium text-pl-accent underline underline-offset-2 hover:text-pl-accent-hover">
												Apply for professional membership
											</Link>
											.
										</p>
									</div>
								</div>
							</header>

							<div className="px-5 pb-8 pt-2 md:px-7 md:pb-10">
								<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
									{active.content}
								</ReactMarkdown>
							</div>
						</article>

						<Link to="/knowledge-hub" className="mt-6 inline-flex items-center gap-2 text-14 font-medium text-pl-accent hover:text-pl-accent-hover">
							<ArrowLeft size={16} aria-hidden="true" />
							All research questions
						</Link>
					</section>
				)}
			</KnowledgeHubLayout>
		</div>
	);
};

export default KnowledgeHub;
